import React, { useState, useEffect, useRef } from 'react';
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import WarningRoundIcon from '@rsuite/icons/WarningRound';
import ArrowRightIcon from '@rsuite/icons/ArrowRight';
import SearchIcon from '@rsuite/icons/Search';
import { Form, Highlight } from 'rsuite';
import {
  CategoryNode,
  isLeafNode,
  findNode,
  traverseSubtree,
  collectNodes,
  isSubtreeFullySelected,
} from './treeHelper';
import { Loader } from 'rsuite';
interface ProductCategoryTreeSelectProps {
  treeData: CategoryNode[];
  value: string[];
  onChange: (selectedIds: string[]) => void;
  expandedKeys?: string[];
  onExpand?: (expanded: string[], node: CategoryNode) => void;
  allowClear?: boolean;
  autoCheckChildren?: boolean;
  showSelectAll?: boolean;
  getLabel?: (node: CategoryNode) => string;
  searchKeyword?: string;
  onSearch?: (keyword: string) => void;
  filterOption?: (keyword: string, node: CategoryNode) => boolean;
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  width?: string | number;
  loading?: boolean;
}

const TreeSelect: React.FC<ProductCategoryTreeSelectProps> = ({
  treeData = [],
  value,
  onChange,
  expandedKeys = [],
  onExpand,
  allowClear = false,
  autoCheckChildren = true,
  showSelectAll = true,
  getLabel,
  searchKeyword: propSearchKeyword = '',
  onSearch,
  filterOption,
  disabled,
  placeholder = '請選擇分類',
  errorMessage,
  width,
  loading = false,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [localTreeData, setLocalTreeData] = useState<CategoryNode[]>(treeData);
  const [localExpandedKeys, setLocalExpandedKeys] =
    useState<string[]>(expandedKeys);
  const [searchKeyword, setSearchKeyword] = useState(propSearchKeyword);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  const handleExpand = (node: CategoryNode) => {
    const isExpanded = localExpandedKeys.includes(node.id);
    const newExpandedKeys = isExpanded
      ? localExpandedKeys.filter((key) => key !== node.id)
      : [...localExpandedKeys, node.id];
    setLocalExpandedKeys(newExpandedKeys);
    onExpand?.(newExpandedKeys, node);
  };

  const handleSelect = async (id: string) => {
    const sel = new Set(value);
    const { node, parent } = findNode(localTreeData, id);
    if (!node) return;

    const checked = sel.has(id);

    // 只有 autoCheckChildren (全選) 時才需要等待 children 載入
    if (autoCheckChildren) {
      // 遍歷子樹勾選/取消，但跳過 disabled 的節點
      traverseSubtree(node, (n) => {
        if (!n.disabled) {
          // 只有非 disabled 的節點才能被勾選/取消
          checked ? sel.delete(n.id) : sel.add(n.id);
        }
      });
    } else {
      // 立即勾選/取消，不等待任何資料
      checked ? sel.delete(id) : sel.add(id);
    }

    // 更新父節點狀態
    let curParent = parent;
    while (curParent) {
      isSubtreeFullySelected(curParent, sel)
        ? sel.add(curParent.id)
        : sel.delete(curParent.id);
      curParent = findNode(localTreeData, curParent.id).parent;
    }

    onChange(Array.from(sel));
  };

  const handleTagRemove = (id: string) => {
    const sel = new Set(value);
    const { node } = findNode(localTreeData, id);
    if (!node) return;

    if (autoCheckChildren) {
      traverseSubtree(node, (n) => {
        if (!n.disabled) {
          // 只有非 disabled 的節點才能被取消
          sel.delete(n.id);
        }
      });
    } else {
      sel.delete(id);
    }
    // 更新父節點狀態
    let curParent = findNode(localTreeData, node.id).parent;
    while (curParent) {
      isSubtreeFullySelected(curParent, sel)
        ? sel.add(curParent.id)
        : sel.delete(curParent.id);
      curParent = findNode(localTreeData, curParent.id).parent;
    }
    onChange(Array.from(sel));
  };

  const getAllLeafIds = () =>
    collectNodes(localTreeData, 'leaf', 'id') as string[];
  const getAllNonLeafIds = () =>
    collectNodes(localTreeData, 'nonLeaf', 'id') as string[];

  const areAllSelected = () => {
    const allIds = [...getAllLeafIds(), ...getAllNonLeafIds()];
    return allIds.length > 0 && allIds.every((id) => value.includes(id));
  };

  // 處理搜尋關鍵字變更
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value.trim().toLowerCase();
    setSearchKeyword(keyword);
    onSearch?.(keyword);
  };

  const renderTree = (nodes: CategoryNode[], level = 0): JSX.Element[] =>
    nodes.flatMap((node) => {
      const isExpanded = localExpandedKeys.includes(node.id);
      // 檢查當前節點是否符合搜尋條件
      const matched = filterOption
        ? filterOption(searchKeyword, node)
        : node.label.toLowerCase().includes(searchKeyword.toLowerCase());

      // 如果節點和子節點都不符合，則過濾掉
      if (searchKeyword && !matched && !isExpanded) return [];

      return [
        <div
          key={node.id}
          className='flex items-center py-1'
          style={{
            marginLeft: `${level * 25}px`,
          }}
        >
          {!isLeafNode(node) ? (
            <span
              onClick={() => handleExpand(node)}
              className='flex size-5 cursor-pointer items-center justify-center rounded-lg hover:bg-gray-200'
            >
              <ArrowRightIcon
                data-testid={`expand-icon-${node.id}`}
                className={`transition-transform duration-300 ${
                  isExpanded ? 'rotate-90' : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpand(node);
                }}
              />
            </span>
          ) : (
            <span className='w-5'></span>
          )}

          <div className='pl-2'>
            {/* 只有子節點才顯示 checkbox */}
            {node.checkable !== false && isLeafNode(node) && (
              <input
                className='ml-2'
                type='checkbox'
                id={node.id}
                checked={value.includes(node.id)}
                disabled={node.disabled}
                onChange={() => handleSelect(node.id)}
              />
            )}
            <label
              htmlFor={node.id}
              className={`flex items-center ${
                isLeafNode(node) && node.disabled
                  ? '!cursor-not-allowed text-gray-400'
                  : ''
              }`}
            >
              <Highlight query={searchKeyword}>
                {getLabel ? getLabel(node) : node.label}
              </Highlight>
            </label>
          </div>
        </div>,

        ...(isExpanded && node.children
          ? [...renderTree(node.children, level + 0.65)]
          : []),
      ];
    });

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
        setSearchKeyword('');
      }
    };

    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);

  // 更新 localTreeData 當 treeData 改變時
  useEffect(() => {
    setLocalTreeData(treeData);
  }, [treeData]);

  useEffect(() => {
    if (!searchKeyword) {
      // 如果 keyword 清空，還原預設展開節點
      setLocalExpandedKeys(expandedKeys);
      return;
    }

    const matchedNodeIds = new Set<string>();
    const expandParentIds = new Set<string>();
    const expandChildIds = new Set<string>();

    const collectMatches = (nodes: CategoryNode[], parentId?: string[]) => {
      nodes.forEach((node) => {
        const label = node.label.toLowerCase();
        const keyword = searchKeyword.toLowerCase();

        const isMatch = label.includes(keyword);

        if (isMatch) {
          matchedNodeIds.add(node.id);
          if (parentId) {
            parentId.forEach((pid) => expandParentIds.add(pid));
          }
          // 展開匹配節點的所有子節點
          if (node.children) {
            traverseSubtree(node, (child) => expandChildIds.add(child.id));
          }
        }

        // 遞歸檢查子節點
        if (node.children) {
          collectMatches(node.children, [...(parentId || []), node.id]);
        }
      });
    };

    collectMatches(localTreeData);
    // 確保展開所有包含符合結果的父節點及子節點
    setLocalExpandedKeys(
      Array.from(
        new Set([...expandParentIds, ...matchedNodeIds, ...expandChildIds]),
      ),
    );
  }, [searchKeyword]);

  // 當 dropdownOpen 為 true 時，立即渲染內容；否則延遲 200ms 後清除內容
  // 這樣可以避免在快速點擊時造成動畫閃爍或不必要的渲染
  // 控制下拉選單渲染
  useEffect(() => {
    if (dropdownOpen) {
      setShouldRender(true);
    } else {
      const t = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(t);
    }
  }, [dropdownOpen]);

  return (
    <div
      className='relative flex w-[380px] items-center justify-center'
      ref={containerRef}
      style={{ width: width || '380px' }}
    >
      {/* 觸發區塊 */}
      <div
        onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
        className={`rs-input !flex items-center justify-between ${disabled ? 'cursor-not-allowed !bg-[#f7f7fa] hover:!border-gray-100' : ''}`}
      >
        {loading && (
          <Loader
            size='xs'
            className='absolute right-10 top-2.5'
            role='status'
          />
        )}
        {value.length ? (
          <div className='flex flex-1 flex-wrap gap-2'>
            {value
              .filter((id) => {
                const node = findNode(localTreeData, id).node;
                return node && isLeafNode(node);
              })
              .map((id) => {
                const node = findNode(localTreeData, id).node!;
                return (
                  <div
                    key={id}
                    className={`flex items-center justify-center rounded-sm bg-gray-200 p-1 text-xs ${disabled ? 'bg-[#f7f7fa] text-[#717273]' : ''}`}
                    data-testid={`tag-${id}`}
                  >
                    {node.label}
                    <WarningRoundIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTagRemove(id);
                      }}
                      data-testid={`remove-tag-${id}`}
                      className={`ml-2 cursor-pointer text-[12px] text-gray-400 hover:text-gray-500 ${disabled ? 'hidden' : ''}`}
                    />
                  </div>
                );
              })}
          </div>
        ) : (
          <span className={`text-gray-500 ${disabled ? 'opacity-85' : ''}`}>
            {placeholder}
          </span>
        )}
        <div className='ml-3 flex items-center'>
          {value.length > 0 && allowClear && !disabled && (
            <WarningRoundIcon
              data-testid='clear-all'
              className='mr-1 cursor-pointer text-[12px] text-gray-500'
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          )}
          <ArrowDownLineIcon
            data-testid='toggle-dropdown'
            className={`cursor-pointer text-[16px] text-gray-500 ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
          />
        </div>
      </div>
      <Form.ErrorMessage show={!!errorMessage} placement='topEnd'>
        {errorMessage}
      </Form.ErrorMessage>
      {/* 下拉選單內容 */}
      {shouldRender && (
        <div
          className={`absolute left-0 top-full z-20 mt-1 max-h-[240px] w-full overflow-y-auto rounded bg-white px-2 pb-2 shadow-lg transition-all duration-500 ease-in-out ${
            dropdownOpen && !disabled
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none invisible translate-y-2 opacity-0'
          }`}
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className='sticky top-0 z-30 items-center bg-white'>
            {/* 搜尋欄 */}
            <div className='mb-2 bg-white pt-2'>
              <input
                type='text'
                value={searchKeyword}
                onChange={handleSearchChange}
                placeholder={loading ? '載入中...' : '搜尋分類'}
                className='rs-input w-full !rounded-[5px] px-3 text-sm'
              />
              {searchKeyword ? (
                <WarningRoundIcon
                  data-testid='clear-search'
                  className='absolute right-3 top-[18px] cursor-pointer text-[12px] text-gray-500 hover:text-gray-700'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchKeyword('');
                  }}
                />
              ) : (
                <SearchIcon className='absolute right-3 top-[18px] text-gray-500' />
              )}
            </div>
            {/* 空資料提示 */}
            {localTreeData.length === 0 && !loading && (
              <div
                className='py-3 text-center text-sm text-gray-400'
                data-testid='empty-state'
              >
                目前尚無資料
              </div>
            )}
            {/* 根節點固定顯示 */}
            <div className=''>
              {renderTree(localTreeData, 0).filter((_, index) => index === 0)}
            </div>
          </div>
          {/* 全選功能 */}
          {showSelectAll && (
            <div className='mb-2 flex items-center gap-2'>
              <input
                type='checkbox'
                id='check-all-global'
                checked={areAllSelected()}
                disabled={localTreeData.every((node) => node.disabled)}
                onChange={() => {
                  const leafIds = getAllLeafIds();
                  const nonLeafIds = getAllNonLeafIds();
                  onChange(
                    areAllSelected()
                      ? []
                      : [...new Set([...leafIds, ...nonLeafIds])],
                  );
                }}
              />
              <label
                htmlFor='check-all-global'
                className={`ml-2 ${
                  localTreeData.every((node) => node.disabled)
                    ? '!cursor-not-allowed text-gray-400'
                    : 'cursor-pointer'
                }`}
              >
                全部
              </label>
            </div>
          )}
          {renderTree(localTreeData, 0).filter((_, index) => index !== 0)}
        </div>
      )}
    </div>
  );
};

export default TreeSelect;
